import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Shield, ChevronRight, ChevronLeft, CheckCircle, BarChart3, TrendingUp, TrendingDown, Minus } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SCALE_LABELS = [
  { value: 1, label: "Strongly Disagree" },
  { value: 2, label: "Disagree" },
  { value: 3, label: "Neutral" },
  { value: 4, label: "Agree" },
  { value: 5, label: "Strongly Agree" },
];

export default function AssessmentPage() {
  const { checkAuth } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const assessmentType = searchParams.get('type') || 'baseline';
  
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [currentPage, setCurrentPage] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);

  const questionsPerPage = 6;
  const totalPages = Math.ceil(questions.length / questionsPerPage);

  const isReassessment = assessmentType === 'week_12' || assessmentType === 'week_24';
  const reassessmentLabel = assessmentType === 'week_12' ? 'Week 12 Reassessment' : assessmentType === 'week_24' ? 'Week 24 Reassessment' : 'Baseline Assessment';

  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        const { data } = await axios.get(`${API}/assessment/questions`, { withCredentials: true });
        setQuestions(data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchQuestions();
  }, []);

  const currentQuestions = questions.slice(
    currentPage * questionsPerPage,
    (currentPage + 1) * questionsPerPage
  );

  const answeredOnPage = currentQuestions.filter(q => answers[q.id] !== undefined).length;
  const totalAnswered = Object.keys(answers).length;
  const progress = questions.length > 0 ? Math.round((totalAnswered / questions.length) * 100) : 0;

  const handleAnswer = (qId, value) => {
    setAnswers(prev => ({ ...prev, [qId]: value }));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/assessment/submit`, { 
        answers, 
        assessment_type: assessmentType 
      }, { withCredentials: true });
      setResults(data);
      await checkAuth();
    } catch (e) {
      console.error(e);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#FF3B30] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Results screen
  if (results) {
    const maxScore = 5;
    const hasGrowth = results.growth && Object.keys(results.growth).length > 0;
    
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-4 py-12">
        <div data-testid="assessment-results" className="w-full max-w-2xl">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-[#22C55E]/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-[#22C55E]" />
            </div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold text-white mb-2">
              {isReassessment ? 'Reassessment Complete' : 'Assessment Complete'}
            </h1>
            <p className="text-[#A3A3A3]">
              {isReassessment ? `Your ${reassessmentLabel} results are in` : "Here's your Executive Presence baseline"}
            </p>
          </div>

          {/* Overall Score */}
          <div className="bg-[#141414] border border-[#262626] rounded-sm p-6 mb-6 text-center">
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#A3A3A3] mb-2">Overall Score</p>
            <p className="text-5xl font-black text-white">{results.overall_score}<span className="text-xl text-[#737373]">/5</span></p>
          </div>

          {/* Dimension Scores with Growth */}
          <div className="bg-[#141414] border border-[#262626] rounded-sm p-6 mb-6 space-y-4">
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#A3A3A3] mb-2">Dimension Breakdown</p>
            {Object.entries(results.dimension_scores || {}).map(([dim, score]) => {
              const pct = (score / maxScore) * 100;
              const isStrength = results.strengths?.includes(dim);
              const isGrowthArea = results.growth_areas?.includes(dim);
              const growthVal = hasGrowth ? results.growth[dim] : null;
              
              return (
                <div key={dim}>
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-white text-sm font-medium flex items-center gap-2">
                      {dim}
                      {isStrength && <span className="text-[#22C55E] text-xs font-bold bg-[#22C55E]/10 px-2 py-0.5 rounded-sm">Strength</span>}
                      {isGrowthArea && <span className="text-[#F59E0B] text-xs font-bold bg-[#F59E0B]/10 px-2 py-0.5 rounded-sm">Growth Area</span>}
                    </span>
                    <span className="text-[#A3A3A3] text-sm flex items-center gap-2">
                      {score}/5
                      {growthVal !== null && growthVal !== 0 && (
                        <span className={`flex items-center gap-0.5 text-xs font-bold ${growthVal > 0 ? 'text-[#22C55E]' : 'text-[#EF4444]'}`}>
                          {growthVal > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                          {growthVal > 0 ? '+' : ''}{growthVal}
                        </span>
                      )}
                      {growthVal === 0 && (
                        <span className="flex items-center gap-0.5 text-xs text-[#737373]">
                          <Minus className="w-3 h-3" /> 0
                        </span>
                      )}
                    </span>
                  </div>
                  <div className="w-full h-2 bg-[#1A1A1A] rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-700 ${isStrength ? 'bg-[#22C55E]' : isGrowthArea ? 'bg-[#F59E0B]' : 'bg-[#FF3B30]'}`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Growth Summary for reassessments */}
          {hasGrowth && (
            <div className="bg-[#141414] border border-[#22C55E]/30 rounded-sm p-6 mb-6">
              <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#22C55E] mb-3">Growth Since Baseline</p>
              <div className="grid grid-cols-3 gap-4">
                {Object.entries(results.growth).map(([dim, val]) => (
                  <div key={dim} className="text-center">
                    <p className={`text-2xl font-bold ${val > 0 ? 'text-[#22C55E]' : val < 0 ? 'text-[#EF4444]' : 'text-[#737373]'}`}>
                      {val > 0 ? '+' : ''}{val}
                    </p>
                    <p className="text-[#737373] text-xs mt-1">{dim.split(' ')[0]}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Insights */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            <div className="bg-[#141414] border border-[#22C55E]/30 rounded-sm p-5">
              <p className="text-[#22C55E] text-xs tracking-[0.2em] uppercase font-bold mb-2">Your Strengths</p>
              {results.strengths?.map(s => (
                <p key={s} className="text-white text-sm mb-1">{s}</p>
              ))}
            </div>
            <div className="bg-[#141414] border border-[#F59E0B]/30 rounded-sm p-5">
              <p className="text-[#F59E0B] text-xs tracking-[0.2em] uppercase font-bold mb-2">Growth Areas</p>
              {results.growth_areas?.map(g => (
                <p key={g} className="text-white text-sm mb-1">{g}</p>
              ))}
            </div>
          </div>

          <button
            data-testid="assessment-continue-button"
            onClick={() => navigate('/dashboard')}
            className="w-full bg-[#FF3B30] hover:bg-[#D62828] text-white font-bold py-3.5 rounded-sm transition-colors flex items-center justify-center gap-2"
          >
            Continue to Dashboard <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-4 py-12">
      <div data-testid="assessment-page" className="w-full max-w-2xl">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Shield className="w-6 h-6 text-[#FF3B30]" />
            <span className="font-heading text-white font-bold text-xl">RiskCoach<span className="text-[#FF3B30]">.ai</span></span>
          </div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-white mb-2">
            {isReassessment ? reassessmentLabel : 'Executive Presence Assessment'}
          </h1>
          <p className="text-[#A3A3A3] text-sm max-w-md mx-auto">
            {isReassessment 
              ? "Time to measure your growth. Answer honestly — compare where you are now to where you started."
              : "Rate each statement honestly. Your results will guide your AI coach to focus on your specific development needs."
            }
          </p>
        </div>

        {/* Progress */}
        <div className="mb-8">
          <div className="flex justify-between text-xs text-[#737373] mb-2">
            <span>{totalAnswered} of {questions.length} answered</span>
            <span>{progress}%</span>
          </div>
          <div className="w-full h-1.5 bg-[#1A1A1A] rounded-full overflow-hidden">
            <div
              className="h-full bg-[#FF3B30] rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Current dimension label */}
        {currentQuestions.length > 0 && (
          <div className="mb-4">
            <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#FF3B30]">
              {currentQuestions[0].dimension}
            </span>
          </div>
        )}

        {/* Questions */}
        <div className="space-y-4 mb-8">
          {currentQuestions.map((q) => (
            <div
              key={q.id}
              data-testid={`assessment-question-${q.id}`}
              className={`bg-[#141414] border rounded-sm p-5 transition-all ${answers[q.id] !== undefined ? 'border-[#FF3B30]/30' : 'border-[#262626]'}`}
            >
              <p className="text-white text-sm font-medium mb-4">{q.text}</p>
              <div className="flex gap-2">
                {SCALE_LABELS.map(({ value, label }) => (
                  <button
                    key={value}
                    data-testid={`answer-${q.id}-${value}`}
                    onClick={() => handleAnswer(q.id, value)}
                    className={`flex-1 py-2.5 rounded-sm text-xs font-bold transition-all border
                      ${answers[q.id] === value
                        ? 'bg-[#FF3B30] text-white border-[#FF3B30]'
                        : 'bg-[#0A0A0A] text-[#A3A3A3] border-[#262626] hover:border-[#FF3B30]/50 hover:text-white'
                      }`}
                    title={label}
                  >
                    {value}
                  </button>
                ))}
              </div>
              <div className="flex justify-between mt-2 text-[10px] text-[#737373]">
                <span>Strongly Disagree</span>
                <span>Strongly Agree</span>
              </div>
            </div>
          ))}
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between">
          <button
            data-testid="assessment-prev-button"
            onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
            disabled={currentPage === 0}
            className="flex items-center gap-2 text-[#A3A3A3] hover:text-white disabled:opacity-30 transition-colors text-sm font-medium"
          >
            <ChevronLeft className="w-4 h-4" /> Previous
          </button>

          <span className="text-[#737373] text-sm">
            Page {currentPage + 1} of {totalPages}
          </span>

          {currentPage < totalPages - 1 ? (
            <button
              data-testid="assessment-next-button"
              onClick={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))}
              className="flex items-center gap-2 text-white bg-[#FF3B30] hover:bg-[#D62828] px-4 py-2 rounded-sm transition-colors text-sm font-bold"
            >
              Next <ChevronRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              data-testid="assessment-submit-button"
              onClick={handleSubmit}
              disabled={totalAnswered < questions.length || submitting}
              className="flex items-center gap-2 text-white bg-[#FF3B30] hover:bg-[#D62828] px-4 py-2 rounded-sm transition-colors text-sm font-bold disabled:opacity-50"
            >
              <BarChart3 className="w-4 h-4" />
              {submitting ? 'Analyzing...' : 'View Results'}
            </button>
          )}
        </div>

        {totalAnswered < questions.length && currentPage === totalPages - 1 && (
          <p className="text-center text-[#737373] text-xs mt-4">
            Answer all {questions.length} questions to see your results
          </p>
        )}
      </div>
    </div>
  );
}
